/**
 * Statement of account for the client detail sheet.
 *
 * Invoices, payments and credit notes with a running balance, pulled live from
 * the connected clients app through the storage seam. The amounts arrive as
 * pre-formatted display strings (`AED 5,250.00`, `AED (400.00)`), so they are
 * rendered as-is rather than re-formatted here. Only mounted when online, so
 * the offline sample workspace never shows an empty panel.
 */
import { useEffect, useState } from 'react';
import { loadStatement } from '../../lib/storage.js';

const TAG = { 'Credit Note': 'cn', Payment: 'pay', Invoice: 'inv' };

export default function ClientStatement({ id }) {
  const [state, setState] = useState({ status: 'loading', items: [], available: true });

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', items: [], available: true });
    loadStatement(id)
      .then(({ items, available }) => { if (live) setState({ status: 'ready', items, available }); })
      .catch(() => { if (live) setState({ status: 'error', items: [], available: true }); });
    return () => { live = false; };
  }, [id]);

  const { status, items, available } = state;

  return (
    <section className="stmt">
      <header className="stmt-head">
        <span>Statement of account</span>
        {status === 'ready' && items.length
          ? <span className="dim">{items.length} transaction{items.length > 1 ? 's' : ''}</span>
          : null}
      </header>

      {status === 'loading' && <div className="stmt-msg">Loading…</div>}
      {status === 'error' && (
        <div className="stmt-msg err">Couldn’t load the statement from the clients app.</div>
      )}
      {status === 'ready' && !available && (
        <div className="stmt-msg">The clients app doesn’t provide a statement endpoint yet.</div>
      )}
      {status === 'ready' && available && !items.length && (
        <div className="stmt-msg">No transactions on record.</div>
      )}

      {status === 'ready' && items.length > 0 && (
        <div className="stmt-scroll">
          <table className="stmt-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Number</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id}>
                  <td className="nowrap">{t.date}</td>
                  <td><span className={`stmt-tag ${TAG[t.type] || 'inv'}`}>{t.type}</span></td>
                  <td className="nowrap">
                    {t.number}
                    {t.originalInvoice ? <span className="dim"> ← {t.originalInvoice}</span> : null}
                  </td>
                  <td>
                    {t.description}
                    {t.dueDate ? <span className="dim"> · due {t.dueDate}</span> : null}
                    {t.status ? <span className="dim"> · {t.status}</span> : null}
                  </td>
                  <td className="num">{t.amountDisplay}</td>
                  <td className="num">{t.runningDisplay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
